import type { ChatMessage } from './chat-types';
import { CONSENT_TEXT } from '../legal-links';

/**
 * How fast a conversation moves, in milliseconds.
 *
 * normal is the login panel's: a visitor who is there to sign in glances at it, and
 * a human pace reads as a conversation rather than a wall of text.
 *
 * quick is signup's. Someone who has clicked "Create an account" has decided and is
 * waiting to get to the form, so the pitch and the lead-ins arrive in about a third
 * of the time. It is still a conversation (the typing indicator shows) but it does
 * not make anyone sit through it.
 */
export const PACES = {
  normal: { first: 500, between: 380, reply: 450, typingBase: 550, typingPerCharacter: 12, typingMax: 1500 },
  quick: { first: 150, between: 140, reply: 180, typingBase: 200, typingPerCharacter: 4, typingMax: 600 },
} as const;

export type Pace = keyof typeof PACES;

// Kept under their old names for the login panel, which only ever uses the normal pace.
export const FIRST_MESSAGE_DELAY = PACES.normal.first;
export const MAX_THREAD_LENGTH = 24;

const characterCount = (message: ChatMessage): number => {
  if (message.kind === 'link') return message.label.length;
  if (message.kind === 'date') return 0;
  if (message.kind === 'consent') return CONSENT_TEXT.length;
  return message.text.length;
};

/** How long the typing indicator shows before a message lands. */
export const typingTime = (message: ChatMessage, pace: Pace = 'normal'): number => {
  // A date label is an annotation, not something Pertento types.
  if (message.kind === 'date') return 0;
  const { typingBase, typingPerCharacter, typingMax } = PACES[pace];
  return Math.min(typingMax, typingBase + characterCount(message) * typingPerCharacter);
};

export const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
